import { Component, ReactNode } from 'react';

interface Props {
  // Human-readable name of the section, used in the fallback message.
  label?: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// Generic section-level error boundary for the WorkerV2 profile.
//
// tenant-admin has no app/route-level error boundary, so before this an
// unguarded render throw in ANY profile card unmounted the ENTIRE WorkerV2
// route, leaving only the app shell/footer (the "blank suspended-worker
// profile" QA reported). Only DependabilityV2Card was protected. Wrapping each
// card in this boundary contains a throw to that one card — the rest of the
// profile, the tabs, and the shell still render, and the failed section shows a
// small recoverable note instead of blanking the page.
class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.warn(
      `[WorkerV2] section "${this.props.label ?? 'section'}" failed to render`,
      error,
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="section-error-fallback"
        style={{
          padding: '16px',
          border: '1px solid #DADADA',
          borderRadius: '8px',
          color: '#6B6B6B',
          fontSize: '14px',
          backgroundColor: '#FFFFFF',
        }}
      >
        {`Couldn't load ${this.props.label ?? 'this section'}. Try refreshing the page.`}
      </div>
    );
  }
}

export default SectionErrorBoundary;
